import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/services/api";
import { API_URL } from "@/constants";
import { useLiveViewersStore } from "@/store/liveViewersStore";
import type { LiveProjectViewers } from "pages/media/analytics/types";

const SEED_STALE_TIME = 1000 * 30;

type LiveViewersSeed = {
  projects: LiveProjectViewers[];
  requestedAt: number;
};

/**
 * Org-wide live viewer count.
 *
 * Seed: one GET returns point-in-time `viewer_count` per project.
 * Live: `liveViewersStore` holds the WS-pushed counts. Per project the newer
 * source wins, so a fresh seed corrects a missed WS update. Without a seed
 * (loading or failed) live values count as is.
 */
export function useLiveViewerCount(organizationId: string | undefined): {
  total: number;
  isLoading: boolean;
  isError: boolean;
} {
  const {
    data: seed,
    isInitialLoading,
    isError,
  } = useQuery<LiveViewersSeed>({
    queryKey: ["live-viewers", organizationId],
    queryFn: async () => {
      // Taken before the request: a live value received during it is newer.
      const requestedAt = Date.now();
      const { data } = await axiosInstance.get<LiveProjectViewers[]>(
        `${API_URL}/analytics/projects/live-analytics/viewers`,
        { params: { organization_id: organizationId } }
      );
      return { projects: data, requestedAt };
    },
    enabled: Boolean(organizationId),
    staleTime: SEED_STALE_TIME,
    retry: false,
  });

  const storeOrganizationId = useLiveViewersStore((s) => s.organizationId);
  const liveByProject = useLiveViewersStore((s) => s.viewersByProject);
  const liveUpdatedAtByProject = useLiveViewersStore(
    (s) => s.updatedAtByProject
  );

  const total = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of seed?.projects ?? []) {
      counts.set(item.project_id, item.viewer_count);
    }
    if (storeOrganizationId === organizationId) {
      for (const [projectId, count] of Object.entries(liveByProject)) {
        const liveUpdatedAt = liveUpdatedAtByProject[projectId] ?? 0;
        // An older live value loses to the seed (absent from the seed = 0).
        if (!seed || liveUpdatedAt >= seed.requestedAt) {
          counts.set(projectId, count);
        }
      }
    }
    return Array.from(counts.values()).reduce((sum, n) => sum + n, 0);
  }, [
    seed,
    storeOrganizationId,
    organizationId,
    liveByProject,
    liveUpdatedAtByProject,
  ]);

  // isInitialLoading stays false for a disabled query (no organization).
  return { total, isLoading: isInitialLoading, isError };
}
